import React, { useState, useEffect } from 'react';
import { formatCurrency, parseCurrency, useEscapeClose } from '../../../utils/SamuiUtils';
import { useModalKeyboardNavigation, scrollToRow, getRowStyle, getKeyboardInstructions } from '../../../utils/ModalKeyboardNavigation';
import { formatDate } from '../../../utils/DateUtils';
import moment from 'moment';

import DatePicker from '../DatePicker';

const InvoiceModal = ({ showInvModal, handleInvClose, onRowSelectInv, onRowSelectSo, invDataList, soDataList, arId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dataType, setDataType] = useState('INV'); // INV = ใบวางบิล, SO = ใบขาย
    const [filteredInvDataList, setFilteredInvDataList] = useState([]);
    const [filteredSoDataList, setFilteredSoDataList] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);

    useEscapeClose(showInvModal, handleInvClose);

    // เพิ่ม keyboard navigation
    useModalKeyboardNavigation(
        showInvModal,
        filteredInvDataList,
        selectedRowIndex,
        setSelectedRowIndex,
        onRowSelectInv,
        handleInvClose
    );

    useEffect(() => {
        setFilteredInvDataList(
            invDataList.filter(inv => {
                const isSearchMatch = inv.Doc_No.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    inv.AR_Name.toLowerCase().includes(searchTerm.toLowerCase());

                const isArIdMatch = parseCurrency(inv.AR_ID) === parseCurrency(arId);

                const isDateInRange = !startDate || !endDate ||
                    moment(inv.Doc_Date).isBetween(startDate, endDate, 'day', '[]');

                return isSearchMatch && isArIdMatch && isDateInRange;
            })
        );
    }, [searchTerm, invDataList, arId, startDate, endDate]);

    useEffect(() => {
        setFilteredSoDataList(
            soDataList.filter(so => {
                const isSearchMatch = so.Doc_No.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    so.AR_Name.toLowerCase().includes(searchTerm.toLowerCase());

                const isArIdMatch = parseCurrency(so.AR_ID) === parseCurrency(arId);

                const isDateInRange = !startDate || !endDate ||
                    moment(so.Doc_Date).isBetween(startDate, endDate, 'day', '[]');

                return isSearchMatch && isArIdMatch && isDateInRange;
            })
        );
    }, [searchTerm, soDataList, arId, startDate, endDate]);

    useEffect(() => {
        if (showInvModal) {
            const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
            const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
            setSearchTerm('');
            setStartDate(startOfMonth);
            setEndDate(endOfMonth);
            setDataType('INV');
        }
    }, [showInvModal]);

    useEffect(() => {
        const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
        setSearchTerm('');
        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
        setDataType(dataType);
    }, [dataType]);

    return (
        <div className={`modal ${showInvModal ? 'show' : ''}`} style={{ display: showInvModal ? 'block' : 'none' }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">รายชื่อใบวางบิล & ใบขาย</h5>
                        <button type="button" className="close" onClick={handleInvClose}>
                            <span>&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-6">
                                <div className="form-group">
                                    <span className="fw-bold">ค้นหาเอกสาร</span>
                                    <input
                                        style={{ width: '100%' }}
                                        type="text"
                                        className="form-control"
                                        placeholder="ค้นหาเลขที่เอกสาร หรือ ชื่อลูกค้า"
                                        value={searchTerm || ''}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="form-group">
                                    <span className="fw-bold">ประเภทข้อมูล</span>
                                    <div className="radio-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="dataType"
                                            value="INV"
                                            checked={dataType === 'INV'}
                                            onChange={(e) => setSearchTerm("") + setDataType(e.target.value)}
                                        />
                                        <label className="form-check-label ml-2">ใบวางบิล</label>
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="dataType"
                                            value="SO"
                                            checked={dataType === 'SO'}
                                            onChange={(e) => setSearchTerm("") + setDataType(e.target.value)}
                                        />
                                        <label className="form-check-label ml-2">ใบขาย (Vansale)</label>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-6">
                                <div className="form-group">
                                    <DatePicker
                                        label="วันที่เริ่มต้น"
                                        name="startDate"
                                        value={startDate}
                                        onChange={(date) => setStartDate(moment(date).format('YYYY-MM-DD'))}
                                        disabled={false}
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group">
                                    <DatePicker
                                        label="วันที่สิ้นสุด"
                                        name="endDate"
                                        value={endDate}
                                        onChange={(date) => setEndDate(moment(date).format('YYYY-MM-DD'))}
                                        disabled={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ใบวางบิล */}
                        {dataType === 'INV' && (
                            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            <th className="text-center" style={{ width: '15%' }}>เลขที่เอกสาร (BI)</th>
                                            <th className="text-center" style={{ width: '10%' }}>วันที่เอกสาร</th>
                                            <th className="text-center" style={{ width: '50%' }}>ชื่อลูกค้า</th>
                                            <th className="text-center" style={{ width: '35%' }}>ยอดรวม</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInvDataList.length > 0 ? (
                                            filteredInvDataList.map((inv, index) => (
                                                <tr
                                                    key={inv.Doc_ID || index + 1}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index);
                                                        onRowSelectInv(inv);
                                                    }}
                                                    style={getRowStyle(index, selectedRowIndex)}>
                                                    <td className="text-center">{inv.Doc_No}</td>
                                                    <td className="text-center">{formatDate(inv.Doc_Date)}</td>
                                                    <td className="text-left">{inv.AR_Name}</td>
                                                    <td className="text-end">{formatCurrency(inv.BL_Net_Total)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center"><h5>ไม่พบข้อมูล</h5></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ใบขาย */}
                        {dataType === 'SO' && (
                            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            <th className="text-center" style={{ width: '15%' }}>เลขที่เอกสาร (SO)</th>
                                            <th className="text-center" style={{ width: '10%' }}>วันที่เอกสาร</th>
                                            <th className="text-center" style={{ width: '32%' }}>ชื่อลูกค้า</th>
                                            <th className="text-center" style={{ width: '32%' }}>รายละเอียดเอกสาร</th>
                                            <th className="text-center" style={{ width: '10%' }}>ราคารวม</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSoDataList.length > 0 ? (
                                            filteredSoDataList.map((so, index) => (
                                                <tr
                                                    key={so.Doc_ID || index + 1}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index);
                                                        onRowSelectSo(so);
                                                    }}
                                                    style={getRowStyle(index, selectedRowIndex)}>
                                                    <td className="text-center">{so.Doc_No}</td>
                                                    <td className="text-center">{formatDate(so.Doc_Date)}</td>
                                                    <td className="text-left">{so.AR_Name}</td>
                                                    <td className="text-left">{so.Doc_Remark1}</td>
                                                    <td className="text-end">{formatCurrency(so.NetTotal)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center"><h5>ไม่พบข้อมูล</h5></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-danger" onClick={handleInvClose}>
                            ปิด
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceModal;